import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  ChevronLeft, ChevronRight, Plus, Sun, Moon, Bed, Car, X, Copy, Save,
  ArrowLeftRight, AlertTriangle, User as UserIcon, HeartPulse, Umbrella, LogOut,
  Search, Users, Calendar as CalIcon, Clock
} from "lucide-react";
import { toast } from "sonner";

const DOW_IT = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const META = {
  DAY:      { label: "Giorno", short: "Giorno", icon: Sun, color: "amber",  chip: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  NIGHT:    { label: "Notte",  short: "Notte",  icon: Moon, color: "sky",   chip: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  REST:     { label: "Riposo", short: "Riposo", icon: Bed, color: "slate",  chip: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  SICK:     { label: "Malattia", short: "Malat.", icon: HeartPulse, color: "rose", chip: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  VACATION: { label: "Ferie", short: "Ferie", icon: Umbrella, color: "indigo", chip: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" },
  LEAVE:    { label: "Permesso", short: "Perm.", icon: LogOut, color: "purple", chip: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
};

// Monday of the current week
function mondayOf(d) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day);
  dt.setDate(dt.getDate() + diff);
  dt.setHours(12,0,0,0);
  return dt;
}
// LOCAL date-only ISO (avoids UTC shift causing wrong day)
function iso(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
}
function addDays(dt, n) { const x = new Date(dt); x.setDate(x.getDate()+n); return x; }
function fmtDMY(dt) { return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${dt.getFullYear()}`; }
// Parse YYYY-MM-DD into a local-noon Date to avoid timezone shift
function parseLocalDate(s) {
  if (!s || typeof s !== "string") return new Date(s);
  const p = s.split("-");
  if (p.length !== 3) return new Date(s);
  return new Date(Number(p[0]), Number(p[1])-1, Number(p[2]), 12, 0, 0, 0);
}

export default function TurnisticaWeek() {
  const today = new Date();
  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [shifts, setShifts] = useState({}); // date|driver_id -> shift row
  const [driverFilter, setDriverFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [newPanel, setNewPanel] = useState(false);
  const [detailCell, setDetailCell] = useState(null); // {driverId, date, shift}
  const [subPanel, setSubPanel] = useState(null); // {shift}
  const [busy, setBusy] = useState(false);

  const weekDays = useMemo(() => Array.from({length:7}, (_,i)=>addDays(weekStart, i)), [weekStart]);
  const weekLabel = `${fmtDMY(weekStart)} - ${fmtDMY(addDays(weekStart,6))}`;

  const load = async () => {
    // fetch shifts for a range spanning weekStart..end (may cross months)
    const start = weekStart, end = addDays(weekStart,6);
    const monthsSet = new Set();
    for (let i=0;i<7;i++) { const d=addDays(weekStart,i); monthsSet.add(`${d.getFullYear()}-${d.getMonth()+1}`); }
    const [drvR, vehR, ...shiftRes] = await Promise.all([
      api.get("/drivers"),
      api.get("/vehicles"),
      ...Array.from(monthsSet).map(ym => { const [y,m] = ym.split("-"); return api.get("/shifts", { params: { year: Number(y), month: Number(m) } }); })
    ]);
    setDrivers(drvR.data.filter(d => d.active !== false));
    setVehicles(vehR.data.filter(v => v.active !== false));
    const map = {};
    shiftRes.forEach(r => (r.data || []).forEach(s => { map[`${s.date}|${s.driver_id}`] = s; }));
    setShifts(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const vehicleById = useMemo(() => Object.fromEntries(vehicles.map(v => [v.id, v])), [vehicles]);
  const driverById = useMemo(() => Object.fromEntries(drivers.map(d => [d.id, d])), [drivers]);

  const visibleDrivers = useMemo(() => {
    let out = drivers;
    if (driverFilter !== "all") out = out.filter(d => d.id === driverFilter);
    return out;
  }, [drivers, driverFilter]);

  const filteredCell = (cell) => {
    if (!cell) return null;
    if (vehicleFilter !== "all" && cell.vehicle_id !== vehicleFilter) return null;
    if (typeFilter !== "all") {
      if (typeFilter === "SUB" && !(cell.substitute_driver_id || cell.substitute_name)) return null;
      if (typeFilter !== "SUB" && cell.shift_type !== typeFilter) return null;
    }
    return cell;
  };

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const thisWeek = () => setWeekStart(mondayOf(new Date()));

  const copyPrev = async () => {
    if (!window.confirm("Copiare i turni della settimana precedente in questa? (autisti+veicoli+orari, escluse sostituzioni)")) return;
    try {
      const r = await api.post("/shifts/copy-previous-week", { week_start: iso(weekStart) });
      toast.success(`${r.data.created} turni copiati`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = (driverId, dateIso) => {
    const s = shifts[`${dateIso}|${driverId}`];
    if (!s) { /* empty cell → open new panel prefilled */
      setNewPanel({ prefill: { driver_id: driverId, date_from: dateIso, date_to: dateIso, days: [parseLocalDate(dateIso).getDay() === 0 ? 6 : parseLocalDate(dateIso).getDay()-1] } });
      return;
    }
    setDetailCell({ driverId, dateIso, shift: s });
  };

  return (
    <div className="space-y-5 pb-16" data-testid="turnistica-week">
      {/* HEADER + NAV */}
      <div className="rounded-2xl border border-slate-800/80 bg-gradient-to-br from-slate-900 to-slate-950 p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30"><CalIcon className="text-amber-400" size={20}/></div>
              <h1 className="font-display text-3xl font-extrabold text-white tracking-tight">Turnistica Autisti</h1>
            </div>
            <div className="text-sm text-slate-400 mt-1.5 ml-12">Gestisci i turni, assegna i veicoli e organizza la tua flotta</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-xl border border-slate-800 bg-slate-950/60 p-1">
              <button onClick={prevWeek} data-testid="prev-week-btn" className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronLeft size={18}/></button>
              <div className="min-w-[220px] text-center font-mono-num font-bold text-white px-3">{weekLabel}</div>
              <button onClick={nextWeek} data-testid="next-week-btn" className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronRight size={18}/></button>
            </div>
            <Button variant="outline" onClick={thisWeek} className="border-slate-800 bg-transparent text-slate-300">Questa settimana</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="new-shift-btn" onClick={()=>setNewPanel({ prefill: null })}><Plus size={16}/> Nuova Turnazione</Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={driverFilter} onValueChange={setDriverFilter}>
            <SelectTrigger className="bg-slate-900 border-slate-800 w-56 h-9"><SelectValue placeholder="Tutti gli autisti"/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti gli autisti</SelectItem>
              {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
            <SelectTrigger className="bg-slate-900 border-slate-800 w-56 h-9"><SelectValue placeholder="Tutti i veicoli"/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti i veicoli</SelectItem>
              {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-slate-900 border-slate-800 w-40 h-9"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti i turni</SelectItem>
              <SelectItem value="DAY">Giorno</SelectItem>
              <SelectItem value="NIGHT">Notte</SelectItem>
              <SelectItem value="REST">Riposo</SelectItem>
              <SelectItem value="SUB">Con sostituzione</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={copyPrev} className="border-slate-800 bg-transparent text-slate-300 h-9"><Copy size={14}/> Copia settimana precedente</Button>
        </div>
      </div>

      {/* GRID */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="week-grid">
            <thead className="bg-slate-950/70">
              <tr>
                <th className="text-left p-3 min-w-[190px] sticky left-0 bg-slate-950/95 z-10 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista</th>
                {weekDays.map((d,i)=> {
                  const isToday = iso(d) === iso(new Date());
                  return (
                    <th key={i} className={`p-2 text-center min-w-[140px] ${isToday ? "bg-amber-500/10" : ""}`}>
                      <div className={`text-xs font-bold uppercase tracking-widest ${isToday ? "text-amber-400" : "text-slate-400"}`}>{DOW_IT[i]}</div>
                      <div className={`font-mono-num text-xs ${isToday ? "text-amber-300" : "text-slate-500"}`}>{String(d.getDate()).padStart(2,"0")}/{String(d.getMonth()+1).padStart(2,"0")}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {visibleDrivers.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-slate-500">Nessun autista</td></tr>}
              {visibleDrivers.map(drv => (
                <tr key={drv.id} className="hover:bg-slate-900/20">
                  <td className="p-3 sticky left-0 bg-slate-950/80 z-10 border-r border-slate-800/60">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm flex-shrink-0">{(drv.first_name||"?")[0]}{(drv.last_name||"?")[0]}</div>
                      <div className="min-w-0">
                        <div className="text-white font-bold text-sm truncate">{drv.first_name} {drv.last_name}</div>
                        {drv.phone && <div className="text-[10px] text-slate-500 font-mono-num truncate">{drv.phone}</div>}
                      </div>
                    </div>
                  </td>
                  {weekDays.map((d,i)=> {
                    const dIso = iso(d);
                    const raw = shifts[`${dIso}|${drv.id}`];
                    const s = filteredCell(raw);
                    return (
                      <td key={i} className="p-1.5">
                        <ShiftCell shift={s} rawShift={raw} filtered={!!raw && !s} onClick={()=>openDetail(drv.id, dIso)} testid={`cell-${dIso}-${drv.id}`}/>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* LEGEND */}
        <div className="border-t border-slate-800/60 px-4 py-2.5 flex flex-wrap gap-3 text-xs">
          <LegendDot color="bg-amber-400" label="Giorno"/>
          <LegendDot color="bg-sky-400" label="Notte"/>
          <LegendDot color="bg-slate-400" label="Riposo"/>
          <LegendDot color="bg-rose-500" label="Sostituzione"/>
          <LegendDot color="bg-indigo-400" label="Ferie"/>
          <LegendDot color="bg-purple-400" label="Permesso"/>
        </div>
      </div>

      {/* PANELS */}
      {newPanel && <NewTurnPanel prefill={newPanel.prefill} drivers={drivers} vehicles={vehicles} weekStart={weekStart} onClose={()=>setNewPanel(null)} onSaved={()=>{ setNewPanel(null); load(); }}/>}
      {detailCell && <DetailPanel cell={detailCell} drivers={drivers} vehicles={vehicles} driverById={driverById} vehicleById={vehicleById} onClose={()=>setDetailCell(null)} onEdit={(s)=>{ setDetailCell(null); setNewPanel({ prefill: { edit: s } }); }} onSubOpen={(s)=>{ setDetailCell(null); setSubPanel({ shift: s }); }} onReload={load}/>}
      {subPanel && <SubstitutionPanel shift={subPanel.shift} drivers={drivers} vehicles={vehicles} onClose={()=>setSubPanel(null)} onSaved={()=>{ setSubPanel(null); load(); }}/>}
    </div>
  );
}

function ShiftCell({ shift, rawShift, filtered, onClick, testid }) {
  if (!shift && !rawShift) {
    return <button type="button" data-testid={testid} onClick={onClick} className="w-full h-[74px] rounded-lg border border-dashed border-slate-800 hover:border-amber-500/50 hover:bg-amber-500/5 text-slate-600 hover:text-amber-400 text-xs flex items-center justify-center transition-all">+</button>;
  }
  if (!shift && filtered) {
    return <div className="w-full h-[74px] rounded-lg border border-dashed border-slate-800/50 opacity-30"/>;
  }
  const s = shift;
  const M = META[s.shift_type] || META.REST;
  const Icon = M.icon;
  const hasSub = !!(s.substitute_driver_id || s.substitute_name);
  const cls = hasSub
    ? "bg-rose-500/10 border-rose-500/50 hover:border-rose-400"
    : `${M.chip} hover:brightness-125`;
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`w-full min-h-[74px] rounded-lg border p-1.5 text-left transition-all ${cls}`}>
      {hasSub ? (
        <>
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-rose-300"><ArrowLeftRight size={10}/> Sostituzione</div>
          <div className="text-white text-xs font-bold truncate mt-0.5">{s.substitute_name || "Sostituto"}</div>
          {(s.time_start && s.time_end) && <div className="text-[10px] font-mono-num text-rose-200">{s.time_start} - {s.time_end}</div>}
          {s.vehicle_plate && <div className="text-[10px] font-mono-num text-white font-bold mt-0.5">{s.vehicle_plate}</div>}
        </>
      ) : (
        <>
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide"><Icon size={10}/> {M.short}</div>
          {(s.time_start && s.time_end) && s.shift_type !== "REST" && <div className="text-[10px] font-mono-num opacity-90 mt-0.5">{s.time_start} - {s.time_end}</div>}
          {s.vehicle_plate && s.shift_type !== "REST" && <div className="text-[10px] font-mono-num font-bold text-white mt-0.5">{s.vehicle_plate}</div>}
        </>
      )}
    </button>
  );
}

function LegendDot({ color, label }) {
  return <span className="inline-flex items-center gap-1.5 text-slate-400"><span className={`w-2 h-2 rounded-full ${color}`}/>{label}</span>;
}

function NewTurnPanel({ prefill, drivers, vehicles, weekStart, onClose, onSaved }) {
  const isEdit = !!prefill?.edit;
  const editShift = prefill?.edit || null;
  const [driverId, setDriverId] = useState(editShift?.driver_id || prefill?.driver_id || "");
  const [dateFrom, setDateFrom] = useState(editShift?.date || prefill?.date_from || iso(weekStart));
  const [dateTo, setDateTo] = useState(editShift?.date || prefill?.date_to || iso(addDays(weekStart,6)));
  const [shiftType, setShiftType] = useState(editShift?.shift_type || "DAY");
  const [timeStart, setTimeStart] = useState(editShift?.time_start || "07:00");
  const [timeEnd, setTimeEnd] = useState(editShift?.time_end || "19:00");
  const [vehicleId, setVehicleId] = useState(editShift?.vehicle_id || "");
  const [days, setDays] = useState(prefill?.days || [0,1,2,3,4]); // Lun-Ven
  const [conflictMsg, setConflictMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const veh = vehicles.find(v => v.id === vehicleId);

  // Vehicle conflict check
  useEffect(() => {
    if (!vehicleId || !driverId || !dateFrom || shiftType === "REST") { setConflictMsg(null); return; }
    api.get("/shifts/vehicle-conflict", { params: { date: dateFrom, vehicle_id: vehicleId, exclude_driver_id: driverId } })
      .then(r => {
        if (r.data.count > 0) {
          const c = r.data.conflicts[0];
          const d = drivers.find(x => x.id === c.driver_id);
          setConflictMsg(`Veicolo già assegnato il ${dateFrom} a ${d ? `${d.first_name} ${d.last_name}` : "un altro autista"}. Puoi comunque confermare.`);
        } else setConflictMsg(null);
      }).catch(() => setConflictMsg(null));
  }, [vehicleId, driverId, dateFrom, shiftType, drivers]);

  const toggleDay = (i) => setDays(prev => prev.includes(i) ? prev.filter(x=>x!==i) : [...prev, i].sort());
  const allDays = () => setDays(days.length === 7 ? [] : [0,1,2,3,4,5,6]);

  const save = async () => {
    if (!driverId) { toast.error("Seleziona un autista"); return; }
    if (isEdit) {
      // Modifica singolo giorno
      setSaving(true);
      try {
        await api.post("/shifts", {
          driver_id: driverId, date: dateFrom, shift_type: shiftType,
          time_start: shiftType !== "REST" ? timeStart : null,
          time_end: shiftType !== "REST" ? timeEnd : null,
          vehicle_id: shiftType !== "REST" ? vehicleId : null,
        });
        toast.success("Turno aggiornato");
        onSaved();
      } catch (e) { toast.error(formatApiError(e)); }
      setSaving(false);
      return;
    }
    // Bulk apply-to-days
    setSaving(true);
    try {
      const start = parseLocalDate(dateFrom), end = parseLocalDate(dateTo);
      const items = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
        const wd = d.getDay() === 0 ? 6 : d.getDay()-1;
        if (!days.includes(wd)) continue;
        items.push({
          driver_id: driverId, date: iso(d), shift_type: shiftType,
          time_start: shiftType !== "REST" ? timeStart : null,
          time_end: shiftType !== "REST" ? timeEnd : null,
          vehicle_id: shiftType !== "REST" ? vehicleId : null,
        });
      }
      if (!items.length) { toast.error("Nessun giorno selezionato nel periodo"); setSaving(false); return; }
      // Use POST /shifts one by one (to persist time+vehicle since bulk doesn't accept them yet)
      for (const it of items) { await api.post("/shifts", it); }
      toast.success(`${items.length} turni creati`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <SidePanel onClose={onClose} title={isEdit ? "Modifica Turno" : "Nuova Turnazione"} subtitle={isEdit ? "Modifica singolo giorno" : "Inserimento Rapido"} testid="new-turn-panel">
      <div className="space-y-4 text-sm">
        <Field label="Autista *">
          <Select value={driverId || "none"} onValueChange={(v)=>setDriverId(v==="none"?"":v)}>
            <SelectTrigger data-testid="np-driver" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona"/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800 z-[100]">
              <SelectItem value="none">-- Seleziona --</SelectItem>
              {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        {!isEdit ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Dal *"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Al *"><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
          </div>
        ) : (
          <Field label="Data"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="bg-slate-900 border-slate-800" disabled/></Field>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Turno *</div>
          <div className="grid grid-cols-3 gap-1.5">
            {["DAY","NIGHT","REST"].map(t => {
              const M = META[t]; const Icon = M.icon;
              return (
                <button key={t} type="button" onClick={()=>setShiftType(t)}
                  data-testid={`np-type-${t}`}
                  className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide transition-all ${shiftType===t ? M.chip + " ring-2 ring-current" : "border-slate-800 bg-slate-900/50 text-slate-400"}`}>
                  <Icon size={14}/> {M.short}
                </button>
              );
            })}
          </div>
        </div>

        {shiftType !== "REST" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Ora inizio"><Input type="time" value={timeStart} onChange={e=>setTimeStart(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Ora fine"><Input type="time" value={timeEnd} onChange={e=>setTimeEnd(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
            </div>

            <Field label="Veicolo">
              <Select value={vehicleId || "none"} onValueChange={(v)=>setVehicleId(v==="none"?"":v)}>
                <SelectTrigger data-testid="np-vehicle" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona veicolo"/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800 z-[100]">
                  <SelectItem value="none">-- Nessuno --</SelectItem>
                  {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</SelectItem>)}
                </SelectContent>
              </Select>
              {veh && (
                <div className="mt-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5 text-xs flex items-center gap-2">
                  <Car size={14} className="text-amber-400"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold truncate">{veh.brand} {veh.model}</div>
                    <div className="text-amber-400 font-mono-num text-[11px]">{veh.plate} {veh.category && <span className="text-slate-500">· {veh.category}</span>} {veh.seats && <span className="text-slate-500">· {veh.seats} posti</span>}</div>
                  </div>
                </div>
              )}
              {conflictMsg && (
                <div data-testid="vehicle-conflict-warn" className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-2 text-xs text-amber-200">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5"/><span>{conflictMsg}</span>
                </div>
              )}
            </Field>
          </>
        )}

        {!isEdit && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Applica a</div>
              <button type="button" onClick={allDays} className="text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300 font-mono-num">{days.length === 7 ? "Deseleziona" : "Seleziona tutti"}</button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {DOW_IT.map((n,i)=> (
                <button key={i} type="button" data-testid={`np-day-${i}`} onClick={()=>toggleDay(i)}
                  className={`py-2 rounded border text-xs font-bold transition ${days.includes(i) ? "bg-amber-500 text-slate-950 border-amber-400" : "bg-slate-900 border-slate-800 text-slate-400"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={onClose}>Annulla</Button>
        <Button data-testid="np-save" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Save size={14}/> {isEdit ? "Salva Modifica" : "Applica Turnazione"}</Button>
      </div>
    </SidePanel>
  );
}

function DetailPanel({ cell, drivers, vehicles, driverById, vehicleById, onClose, onEdit, onSubOpen, onReload }) {
  const s = cell.shift;
  const M = META[s.shift_type] || META.REST;
  const drv = driverById[s.driver_id];
  const hasSub = !!(s.substitute_driver_id || s.substitute_name);
  const subDrv = s.substitute_driver_id ? driverById[s.substitute_driver_id] : null;
  const doDelete = async () => {
    if (!window.confirm("Eliminare questo turno?")) return;
    try { await api.delete(`/shifts/${s.id}`); toast.success("Turno eliminato"); onClose(); onReload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeSub = async () => {
    if (!window.confirm("Rimuovere la sostituzione?")) return;
    try {
      await api.post("/shifts", { driver_id: s.driver_id, date: s.date, shift_type: s.shift_type,
        time_start: s.time_start, time_end: s.time_end, vehicle_id: s.vehicle_id,
        substitute_driver_id: null, substitute_name: null });
      toast.success("Sostituzione rimossa"); onClose(); onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <SidePanel onClose={onClose} title="Dettagli Turno" subtitle={`${cell.dateIso}`} testid="detail-panel" wide>
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${M.chip}`}><M.icon size={11}/> {M.label}</span>
          {hasSub && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-rose-500/50 bg-rose-500/15 text-rose-300 text-[11px] font-bold uppercase tracking-wider"><ArrowLeftRight size={11}/> Sostituzione attiva</span>}
        </div>

        <SectionCard title="Autista originale">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold">{drv ? (drv.first_name||"?")[0]+(drv.last_name||"?")[0] : "?"}</div>
            <div>
              <div className="text-white font-bold">{drv ? `${drv.first_name} ${drv.last_name}` : "-"}</div>
              {drv?.phone && <div className="text-xs text-slate-400 font-mono-num">{drv.phone}</div>}
            </div>
          </div>
        </SectionCard>

        {hasSub && (
          <SectionCard title="Autista effettivo" tone="rose">
            <div className="text-white font-bold">{subDrv ? `${subDrv.first_name} ${subDrv.last_name}` : s.substitute_name}</div>
            {s.substitute_ext_phone && <div className="text-xs text-slate-400 font-mono-num mt-0.5">📞 {s.substitute_ext_phone}</div>}
            {s.substitute_ext_company && <div className="text-xs text-slate-400 mt-0.5">🏢 {s.substitute_ext_company}</div>}
            {s.substitute_ext_compensation != null && <div className="text-xs text-emerald-400 font-mono-num mt-0.5">💶 €{s.substitute_ext_compensation}</div>}
            <button onClick={removeSub} className="mt-2 text-xs text-rose-400 hover:text-rose-300 underline">Rimuovi sostituzione</button>
          </SectionCard>
        )}

        <div className="grid grid-cols-2 gap-2">
          <SectionCard title="Turno"><div className="text-white font-bold">{M.label}</div>{s.time_start && s.time_end && <div className="text-xs text-slate-400 font-mono-num mt-0.5">{s.time_start} - {s.time_end}</div>}</SectionCard>
          {s.vehicle_id && (() => { const v = vehicleById[s.vehicle_id]; return (
            <SectionCard title="Veicolo">
              <div className="text-white font-bold truncate">{v ? `${v.brand} ${v.model}` : "-"}</div>
              <div className="text-amber-400 font-mono-num text-xs">Targa: {s.vehicle_plate || v?.plate || "-"}</div>
            </SectionCard>
          ); })()}
        </div>

        {s.note && <SectionCard title="Note"><div className="text-slate-300 whitespace-pre-wrap">{s.note}</div></SectionCard>}
      </div>

      <div className="mt-5 space-y-2">
        <Button data-testid="dp-edit" onClick={()=>onEdit(s)} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Modifica Turno</Button>
        <Button data-testid="dp-sub" onClick={()=>onSubOpen(s)} variant="outline" className="w-full border-slate-800 bg-transparent text-slate-300"><ArrowLeftRight size={14}/> Gestisci Sostituzione</Button>
        <Button data-testid="dp-delete" onClick={doDelete} variant="outline" className="w-full border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">Elimina Turno</Button>
      </div>
    </SidePanel>
  );
}

function SubstitutionPanel({ shift, drivers, vehicles, onClose, onSaved }) {
  const [mode, setMode] = useState("internal"); // internal | external
  const [subDriverId, setSubDriverId] = useState(shift.substitute_driver_id || "");
  const [extName, setExtName] = useState(shift.substitute_name || "");
  const [extPhone, setExtPhone] = useState(shift.substitute_ext_phone || "");
  const [extCompany, setExtCompany] = useState(shift.substitute_ext_company || "");
  const [extComp, setExtComp] = useState(shift.substitute_ext_compensation || "");
  const [keepVehicle, setKeepVehicle] = useState(true);
  const [newVehicleId, setNewVehicleId] = useState(shift.vehicle_id || "");
  const [note, setNote] = useState(shift.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        driver_id: shift.driver_id, date: shift.date, shift_type: shift.shift_type,
        time_start: shift.time_start, time_end: shift.time_end,
        substitute_driver_id: mode === "internal" ? (subDriverId || null) : null,
        substitute_name: mode === "external" ? (extName || null) : null,
        substitute_ext_phone: mode === "external" ? (extPhone || null) : null,
        substitute_ext_company: mode === "external" ? (extCompany || null) : null,
        substitute_ext_compensation: mode === "external" ? (extComp || null) : null,
        vehicle_id: keepVehicle ? shift.vehicle_id : (newVehicleId || null),
        original_vehicle_id: shift.vehicle_id,
        original_vehicle_plate: shift.vehicle_plate,
        note: note || null,
      };
      if (mode === "internal" && !subDriverId) { toast.error("Seleziona l'autista sostituto"); setSaving(false); return; }
      if (mode === "external" && !extName) { toast.error("Nome sostituto obbligatorio"); setSaving(false); return; }
      await api.post("/shifts", payload);
      toast.success("Sostituzione salvata");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <SidePanel onClose={onClose} title={`Sostituisci Autista - ${shift.date}`} testid="sub-panel" wide>
      <div className="space-y-4 text-sm">
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-900/60 p-1">
          <button type="button" onClick={()=>setMode("internal")} data-testid="sub-mode-int" className={`flex-1 px-3 py-1.5 rounded text-xs font-bold ${mode==="internal"?"bg-amber-500 text-slate-950":"text-slate-400"}`}>Autista interno</button>
          <button type="button" onClick={()=>setMode("external")} data-testid="sub-mode-ext" className={`flex-1 px-3 py-1.5 rounded text-xs font-bold ${mode==="external"?"bg-amber-500 text-slate-950":"text-slate-400"}`}>Autista esterno</button>
        </div>

        {mode === "internal" ? (
          <Field label="Seleziona autista interno *">
            <Select value={subDriverId || "none"} onValueChange={(v)=>setSubDriverId(v==="none"?"":v)}>
              <SelectTrigger data-testid="sub-int-select" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona"/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                <SelectItem value="none">-- Nessuno --</SelectItem>
                {drivers.filter(d => d.id !== shift.driver_id).map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Nome e Cognome *"><Input data-testid="sub-ext-name" value={extName} onChange={e=>setExtName(e.target.value)} className="bg-slate-900 border-slate-800" placeholder="Giovanni Russo"/></Field>
              <Field label="Telefono"><Input value={extPhone} onChange={e=>setExtPhone(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Azienda / Cooperativa"><Input value={extCompany} onChange={e=>setExtCompany(e.target.value)} className="bg-slate-900 border-slate-800" placeholder="Coop. Roma NCC"/></Field>
              <Field label="Compenso (€)"><Input type="number" step="0.01" value={extComp} onChange={e=>setExtComp(e.target.value)} className="bg-slate-900 border-slate-800"/></Field>
            </div>
          </>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Veicolo</div>
          <div className="space-y-2">
            <label className="flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 cursor-pointer">
              <input type="radio" checked={keepVehicle} onChange={()=>setKeepVehicle(true)} className="mt-1"/>
              <div className="flex-1">
                <div className="text-slate-200 text-xs font-bold">Mantieni veicolo originale</div>
                <div className="text-[10px] text-amber-400 font-mono-num">{shift.vehicle_plate || "Nessun veicolo"}</div>
              </div>
            </label>
            <label className="flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 cursor-pointer">
              <input type="radio" checked={!keepVehicle} onChange={()=>setKeepVehicle(false)} className="mt-1"/>
              <div className="flex-1">
                <div className="text-slate-200 text-xs font-bold mb-1">Cambia veicolo</div>
                {!keepVehicle && (
                  <Select value={newVehicleId || "none"} onValueChange={(v)=>setNewVehicleId(v==="none"?"":v)}>
                    <SelectTrigger className="bg-slate-900 border-slate-800 h-8"><SelectValue placeholder="Seleziona veicolo"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800 z-[100]">
                      {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </label>
          </div>
        </div>

        <Field label="Note (opzionale)"><Textarea rows={2} value={note} onChange={e=>setNote(e.target.value)} className="bg-slate-900 border-slate-800" placeholder="Motivo/note sostituzione"/></Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={onClose}>Annulla</Button>
        <Button data-testid="sub-save" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Save size={14}/> Conferma Sostituzione</Button>
      </div>
    </SidePanel>
  );
}

function SidePanel({ title, subtitle, onClose, children, testid, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70]" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose}/>
      <aside className={`absolute right-0 top-0 h-full w-full ${wide ? "sm:w-[440px]" : "sm:w-[400px]"} md:w-[420px] bg-[#0B0F17] border-l border-slate-800/80 shadow-2xl flex flex-col`}
        onClick={(e)=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-800 flex items-start justify-between">
          <div>
            <div className="text-white font-display text-lg font-extrabold">{title}</div>
            {subtitle && <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  );
}

function Field({ label, children }) { return (<div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{label}</div>{children}</div>); }
function SectionCard({ title, children, tone }) {
  const t = tone === "rose" ? "border-rose-500/40 bg-rose-500/5" : "border-slate-800 bg-slate-900/40";
  return (
    <div className={`rounded-xl border p-3 ${t}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">{title}</div>
      {children}
    </div>
  );
}
